import { useMemo, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { addDays, differenceInDays, isAfter, parseISO, startOfDay } from 'date-fns';
import { useApp } from '@/contexts/AppContext';
import SectionHeroCard from '@/components/SectionHeroCard';
import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { buildFamilyCustodyScopeFilter, mapFamilyCustodyShareRow } from '@/lib/family-custody';

export default function FamilyJourneyCard({
  onAddEvent,
  onOpenFamilyLife,
  onOpenHomeJobs,
  action,
}: {
  onAddEvent: () => void;
  onOpenFamilyLife: () => void;
  onOpenHomeJobs: () => void;
  action?: ReactNode;
}) {
  const navigate = useNavigate();
  const { data, user } = useApp();
  const children = data.profile.children ?? [];
  const dependantTypes = data.profile.dependantTypes ?? [];
  const partnerName = data.profile.partnerName ?? '';
  const events = data.events ?? [];
  const chores = data.chores ?? [];
  const [adminItemCount, setAdminItemCount] = useState(0);
  const [structuredProfileCount, setStructuredProfileCount] = useState(0);
  const [adultDependantProfileCount, setAdultDependantProfileCount] = useState(0);
  const [coParentingItemCount, setCoParentingItemCount] = useState(0);
  const [custodyEventCount, setCustodyEventCount] = useState(0);
  const [openCustodyRequestCount, setOpenCustodyRequestCount] = useState(0);
  const [careReviewCount, setCareReviewCount] = useState(0);
  const [careReviewDueSoonCount, setCareReviewDueSoonCount] = useState(0);
  const upcomingEventCount = useMemo(() => events.filter((event) => {
    const date = parseISO(event.date);
    return isAfter(date, startOfDay(new Date())) && date <= addDays(new Date(), 30);
  }).length, [events]);
  const activeChoreCount = useMemo(() => chores.filter((chore) => !chore.done).length, [chores]);
  const schoolEventCount = useMemo(() => events.filter((event) => event.type === 'school').length, [events]);
  useEffect(() => {
    if (!user) return;

    async function fetchJourneyState() {
      const today = startOfDay(new Date()).toISOString().split('T')[0];
      const { data: custodyShareRow } = await supabase
        .from('family_custody_shares' as any)
        .select('*')
        .or(`owner_user_id.eq.${user.id},member_user_id.eq.${user.id}`)
        .maybeSingle();
      const custodyShare = mapFamilyCustodyShareRow(custodyShareRow);
      const custodyScope = buildFamilyCustodyScopeFilter(user.id, custodyShare?.id || null);
      const [{ count }, { data: childProfiles }, { count: adultDependantCount }, { count: coParentingCount }, { count: custodyCount }, { count: proposedRequestCount }, { data: careReviews }] = await Promise.all([
        supabase
          .from('family_admin_items' as any)
          .select('id', { count: 'exact', head: true })
          .eq('user_id', user.id)
          .neq('status', 'done'),
        supabase
          .from('children')
          .select('id, name, date_of_birth, nhs_number, gp_surgery, school_name, emergency_contacts, allergies, medications, notes')
          .eq('user_id', user.id),
        supabase
          .from('adult_dependants')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', user.id),
        supabase
          .from('family_admin_items' as any)
          .select('id', { count: 'exact', head: true })
          .eq('user_id', user.id)
          .eq('area', 'co-parenting')
          .neq('status', 'done'),
        supabase
          .from('family_custody_events' as any)
          .select('id', { count: 'exact', head: true })
          .or(custodyScope)
          .gte('handover_date', today)
          .neq('status', 'done'),
        supabase
          .from('family_custody_requests' as any)
          .select('id', { count: 'exact', head: true })
          .or(custodyScope)
          .eq('status', 'proposed'),
        supabase
          .from('family_care_reviews' as any)
          .select('id, due_date, reminder_days_before, status')
          .eq('user_id', user.id),
      ]);

      const childProfileCount = (childProfiles || []).filter((child) =>
        child.name?.trim().length > 0 && Boolean(
          child.date_of_birth
          || child.nhs_number
          || child.gp_surgery
          || child.school_name
          || child.emergency_contacts
          || child.allergies
          || child.medications
          || child.notes
        )
      ).length;

      const openCareReviews = ((careReviews || []) as any[]).filter((review) => review.status !== 'done');
      const dueSoonCareReviews = openCareReviews.filter((review) =>
        differenceInDays(parseISO(review.due_date), startOfDay(new Date())) <= (review.reminder_days_before ?? 21)
      );

      setAdminItemCount(count || 0);
      setStructuredProfileCount(childProfileCount + (adultDependantCount || 0));
      setAdultDependantProfileCount(adultDependantCount || 0);
      setCoParentingItemCount(coParentingCount || 0);
      setCustodyEventCount(custodyCount || 0);
      setOpenCustodyRequestCount(proposedRequestCount || 0);
      setCareReviewCount(openCareReviews.length);
      setCareReviewDueSoonCount(dueSoonCareReviews.length);
    }

    void fetchJourneyState();
    const handleRefresh = () => {
      void fetchJourneyState();
    };

    window.addEventListener('adulting:data-changed', handleRefresh);
    return () => window.removeEventListener('adulting:data-changed', handleRefresh);
  }, [user]);

  const journey = useMemo(() => {
    const hasFamilyContext = Boolean(
      partnerName.trim() ||
      children.some((child) => child.name.trim()) ||
      dependantTypes.length > 0
    );
    const hasCoParentingContext = coParentingItemCount > 0 || custodyEventCount > 0 || openCustodyRequestCount > 0;

    if (!hasFamilyContext) {
      return {
        title: 'Set up your family and care admin base',
        description: 'Start by recording who depends on you, then add the next event or task that is easiest to forget.',
        items: [
          { label: 'Save partner, child, or dependant details', done: false },
          { label: 'Add the next important event', done: false },
          { label: 'Create one family admin item', done: false },
        ],
        actions: [
          { label: 'Edit profile', onClick: () => navigate('/profile'), primary: true },
          { label: 'Open family life', onClick: onOpenFamilyLife },
        ],
      };
    }

    if (structuredProfileCount === 0) {
      return {
        title: 'Build the family profile layer next',
        description: 'Your basic family context is there. Now save the school, GP, care, and emergency detail you would want quickly to hand.',
        items: [
          { label: 'Family context saved', done: true },
          { label: 'Add one detailed child or adult dependant profile', done: false },
          { label: 'Family admin board started', done: adminItemCount > 0 },
        ],
        actions: [
          { label: 'Open family life', onClick: onOpenFamilyLife, primary: true },
          { label: 'Edit profile', onClick: () => navigate('/profile') },
        ],
      };
    }

    if (adminItemCount === 0) {
      return {
        title: 'Build the family admin layer next',
        description: 'Your family details are there. Now get the next school, childcare, co-parenting, or care task into the admin board so it does not live only in your head.',
        items: [
          { label: 'Family details saved', done: true },
          { label: 'Add your next family admin item', done: false },
          { label: 'Keep one home job visible', done: activeChoreCount > 0 },
        ],
        actions: [
          { label: 'Open family life', onClick: onOpenFamilyLife, primary: true },
          { label: 'Edit profile', onClick: () => navigate('/profile') },
        ],
      };
    }

    if (hasCoParentingContext && custodyEventCount === 0) {
      return {
        title: 'Put the next handover in the calendar',
        description: 'Your co-parenting admin has started. Now keep the next handover date visible in the Family calendar so the trail is not split across notes and messages.',
        items: [
          { label: 'Family details saved', done: true },
          { label: 'Co-parenting admin started', done: true },
          { label: 'Next handover added', done: false },
        ],
        actions: [
          { label: 'Open calendar', onClick: () => navigate('/family'), primary: true },
          { label: 'Open family life', onClick: onOpenFamilyLife },
        ],
      };
    }

    if (hasCoParentingContext && openCustodyRequestCount > 0) {
      return {
        title: 'Your co-parenting trail is active',
        description: 'Keep the next handover visible and leave schedule changes in the written request log so the follow-up trail stays clear later.',
        items: [
          { label: 'Family details saved', done: true },
          { label: 'Next handover visible', done: custodyEventCount > 0 },
          { label: 'Request trail active', done: openCustodyRequestCount > 0 },
        ],
        actions: [
          { label: 'Open calendar', onClick: () => navigate('/family'), primary: true },
          { label: 'Open family life', onClick: onOpenFamilyLife },
        ],
      };
    }

    if (adultDependantProfileCount > 0 && careReviewCount === 0) {
      return {
        title: 'Add the next care review date',
        description: 'The adult-dependant details are there. Now save the next review, assessment, or medication check so caring admin is visible before it gets urgent.',
        items: [
          { label: 'Adult dependant profile saved', done: true },
          { label: 'Next care review visible', done: false },
          { label: 'Family admin board started', done: adminItemCount > 0 },
        ],
        actions: [
          { label: 'Open family life', onClick: onOpenFamilyLife, primary: true },
          { label: 'Open support hub', onClick: () => navigate('/guides?section=family') },
        ],
      };
    }

    if (adultDependantProfileCount > 0 && careReviewDueSoonCount > 0) {
      return {
        title: 'Your caring review trail is active',
        description: 'Keep the next review, assessment, or medication check visible and use the guides when care needs change faster than the paperwork does.',
        items: [
          { label: 'Adult dependant profile saved', done: true },
          { label: 'Care review tracker active', done: careReviewCount > 0 },
          { label: 'At least one review due soon', done: careReviewDueSoonCount > 0 },
        ],
        actions: [
          { label: 'Open family life', onClick: onOpenFamilyLife, primary: true },
          { label: 'Open support hub', onClick: () => navigate('/guides?section=family') },
        ],
      };
    }

    if (upcomingEventCount === 0) {
      return {
        title: 'Put the next family deadline in the diary',
        description: 'Your family admin is moving. The next useful step is making sure the next appointment, school date, or birthday is visible in the calendar too.',
        items: [
          { label: 'Family details saved', done: true },
          { label: 'Family admin board started', done: adminItemCount > 0 },
          { label: 'Add your next important event', done: false },
        ],
        actions: [
          { label: 'Add event', onClick: onAddEvent, primary: true },
          { label: 'Open family life', onClick: onOpenFamilyLife },
        ],
      };
    }

    return {
      title: 'Your family admin is moving',
      description: 'You already have people and dates in the system. Keep family life current here, and keep the home jobs visible in Home so the practical layer stays joined up.',
      items: [
        { label: 'Family details saved', done: true },
        { label: 'Upcoming events tracked', done: upcomingEventCount > 0 },
        { label: 'Family admin board active', done: adminItemCount > 0 },
        ...(hasCoParentingContext ? [{ label: 'Shared-custody handovers visible', done: custodyEventCount > 0 }] : []),
        ...(adultDependantProfileCount > 0 ? [{ label: 'Care reviews visible', done: careReviewCount > 0 }] : []),
        { label: 'School dates visible if needed', done: schoolEventCount > 0 || !children.some((child) => child.name.trim()) },
      ],
      actions: [
        { label: 'Add event', onClick: onAddEvent, primary: true },
        { label: 'Open family life', onClick: onOpenFamilyLife },
        { label: 'Open home jobs', onClick: onOpenHomeJobs },
      ],
    };
  }, [activeChoreCount, adminItemCount, adultDependantProfileCount, careReviewCount, careReviewDueSoonCount, children, coParentingItemCount, custodyEventCount, dependantTypes, navigate, onAddEvent, onOpenFamilyLife, onOpenHomeJobs, openCustodyRequestCount, partnerName, schoolEventCount, structuredProfileCount, upcomingEventCount]);

  return (
    <SectionHeroCard
      title="My People"
      subtitle="Everyone in my life, everything coming up"
      action={action}
      stats={[
        {
          label: 'Events',
          value: String(upcomingEventCount),
          tone: upcomingEventCount > 0 ? 'accent' : 'default',
        },
        {
          label: 'Admin',
          value: String(adminItemCount),
          tone: adminItemCount > 0 ? 'danger' : 'success',
        },
        {
          label: 'Profiles',
          value: String(structuredProfileCount),
          tone: structuredProfileCount > 0 ? 'accent' : 'default',
        },
      ]}
      actions={journey.actions}
    />
  );
}
